/**
 * Real position on the resort plan.
 *
 * Short on purpose: the hard part is having a drawing whose geometry is true,
 * and that was solved upstream in `scripts/build-resort-plan.mjs` by drawing the
 * plan *from* coordinates. The projection below is that generator's exact
 * inverse — nothing here is fitted, so there are no residuals to grow as you
 * walk away from a calibration point. (The hand-drawn plan it replaced could not
 * have supported this at any quality: aspect 1.26 against the ground's 0.84, so
 * a dot would have been right only where you calibrated. Wrong *confidently*,
 * which is worse than absent — guests trust a blue dot more than a map.)
 *
 * TWO ERRORS STACK, and only one was fixed: the plan's own, now a few metres
 * (OSM footprints are volunteer traces, not a survey), and the phone's, ±5–10 m
 * outdoors and worse against a building. On a 400 m site that is "you are at the
 * Infinity Lawn", never "table six", and it is useless indoors.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FEATURES,
  ORIGIN,
  M_PER_DEG_LAT,
  M_PER_DEG_LON,
  ROTATE_DEG,
  VIEW,
  type PlanFeature,
} from '../data/resort-plan';

/**
 * The plan's frame is turned `ROTATE_DEG` clockwise off true north/east (see the
 * generator), and every conversion here must apply the same turn. Get the sign
 * backwards and the dot still lands plausibly *somewhere on the resort*, just
 * rotated about the origin — no error, nothing on screen that looks broken.
 * Hence importing the constant rather than restating it.
 */
const ROT = (ROTATE_DEG * Math.PI) / 180;
const ROT_COS = Math.cos(ROT);
const ROT_SIN = Math.sin(ROT);

/** Metres, in the plan's own frame: x north of ORIGIN, y east of ORIGIN. */
export interface PlanPoint {
  x: number;
  y: number;
}

/** Percentages of the view box — what the DOM needs for `left`/`top`. */
export interface PlanPercent {
  x: number;
  y: number;
}

/**
 * Over 400 m a plate-carrée with a cosine correction is exact to well under the
 * dot's width. The cosine is not optional: at 8.7°N a degree of longitude is
 * ~1.2% shorter than one of latitude, about 5 m across this site — comparable to
 * the GPS error being displayed.
 */
export function planFromLatLon(lat: number, lon: number): PlanPoint {
  const north = (lat - ORIGIN.lat) * M_PER_DEG_LAT;
  const east = (lon - ORIGIN.lon) * M_PER_DEG_LON;
  return {
    x: north * ROT_COS - east * ROT_SIN,
    y: north * ROT_SIN + east * ROT_COS,
  };
}

/** The inverse. Used by tests and the `?at=` debug helpers; having it makes the
 *  round trip checkable rather than assumed. */
export function latLonFromPlan(p: PlanPoint): { lat: number; lon: number } {
  const north = p.x * ROT_COS + p.y * ROT_SIN;
  const east = -p.x * ROT_SIN + p.y * ROT_COS;
  return {
    lat: ORIGIN.lat + north / M_PER_DEG_LAT,
    lon: ORIGIN.lon + east / M_PER_DEG_LON,
  };
}

export function percentFromPlan(p: PlanPoint): PlanPercent {
  return {
    x: ((p.x - VIEW.x) / VIEW.w) * 100,
    y: ((p.y - VIEW.y) / VIEW.h) * 100,
  };
}

export function percentFromLatLon(lat: number, lon: number): PlanPercent {
  return percentFromPlan(planFromLatLon(lat, lon));
}

/** Metres as a percentage of plan width, for the accuracy circle. Plan units
 *  *are* metres, which is why this is a division and not a calibration. */
export function metresToPercent(m: number): number {
  return (m / VIEW.w) * 100;
}

/**
 * Load-bearing rather than defensive: without it a guest at Phuket airport gets
 * a dot clamped to the plan's edge, reading as "you are at the north-west corner
 * of the resort" rather than "you are not there yet". The margin is generous
 * because the beach and approach road are legitimately outside the boundary.
 */
export function isOnSite(p: PlanPoint, marginM = 150): boolean {
  return (
    p.x >= VIEW.x - marginM &&
    p.x <= VIEW.x + VIEW.w + marginM &&
    p.y >= VIEW.y - marginM &&
    p.y <= VIEW.y + VIEW.h + marginM
  );
}

/** Percentages back into plan metres — the picker's half of the round trip, as
 *  a tap arrives as a fraction of the stage. */
export function planFromPercent(p: PlanPercent): PlanPoint {
  return { x: VIEW.x + (p.x / 100) * VIEW.w, y: VIEW.y + (p.y / 100) * VIEW.h };
}

function inPolygon({ x, y }: PlanPoint, ring: readonly (readonly [number, number])[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-18) + xi) inside = !inside;
  }
  return inside;
}

/**
 * The building under a point. Exists so identifying a block is a tap rather than
 * transcribing an OSM way id like `172672606` by hand into resort-wings.ts,
 * where one wrong digit silently highlights the wrong building.
 *
 * Last match wins: features are drawn in ascending z-order, so the last one is
 * visibly on top and therefore the one being pointed at.
 */
export function buildingAt(p: PlanPoint): PlanFeature | null {
  let hit: PlanFeature | null = null;
  for (const f of FEATURES) {
    if (!f.closed) continue;
    if (f.kind !== 'block' && f.kind !== 'pale') continue;
    if (inPolygon(p, f.pts)) hit = f;
  }
  return hit;
}

export type LocateState =
  | 'idle'
  | 'asking'
  | 'watching'
  | 'denied'
  | 'unavailable'
  /** The page is not on a secure origin, so the browser will not even ask. */
  | 'insecure'
  | 'offsite'
  | 'error';

export interface Fix {
  pct: PlanPercent;
  /** The browser's own accuracy estimate, in metres. */
  accuracyM: number;
  /** That radius as a percentage of plan width, ready to draw. */
  accuracyPct: number;
}

/**
 * Watch the guest's position, in plan percentages. `watchPosition`, not
 * `getCurrentPosition`: a single fix taken indoors is usually the worst one they
 * will get all evening.
 */
export function useLiveLocation(): {
  state: LocateState;
  fix: Fix | null;
  start: () => void;
  stop: () => void;
} {
  const [state, setState] = useState<LocateState>('idle');
  const [fix, setFix] = useState<Fix | null>(null);
  const watchId = useRef<number | null>(null);
  /** Whether the guest asked to be followed, as distinct from whether a watch
   *  is currently running. They come apart while the tab is hidden. */
  const wanted = useRef(false);

  const stop = useCallback(() => {
    wanted.current = false;
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setState('idle');
    setFix(null);
  }, []);

  const start = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState('unavailable');
      return;
    }
    /**
     * Check the origin before asking, because the browser will not. Geolocation
     * needs a secure context, and the dev server on a LAN IP — exactly how this
     * gets tested on a phone — is not one. Chrome and Safari refuse it and
     * report PERMISSION_DENIED, indistinguishable from a real refusal, sending
     * the guest hunting through settings that were never the problem.
     */
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setState('insecure');
      return;
    }
    wanted.current = true;
    if (watchId.current !== null) return;
    setState('asking');
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const plan = planFromLatLon(pos.coords.latitude, pos.coords.longitude);
        if (!isOnSite(plan)) {
          setFix(null);
          setState('offsite');
          return;
        }
        const accuracyM = Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : 0;
        setFix({
          pct: percentFromPlan(plan),
          accuracyM,
          accuracyPct: metresToPercent(accuracyM),
        });
        setState('watching');
      },
      (err) => {
        setFix(null);
        setState(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      // `maximumAge: 0`: a cached fix from the airport is the exact wrong
      // answer when the question is where you are standing now.
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }, []);

  /**
   * Drop the watch while the page is hidden, resume on return.
   *
   * `enableHighAccuracy` keeps the GPS radio warm, the most expensive thing a
   * page can ask of a phone, and a guest who taps follow then pockets the phone
   * for the evening pays for fixes nobody looks at. A flat battery at a wedding
   * costs them the schedule and their photos, not just this map.
   *
   * `wanted` survives the pause, so returning to the tab resumes rather than
   * silently doing nothing. Gallery polling pauses on the same event
   * (lib/media.ts).
   */
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        if (watchId.current !== null) {
          navigator.geolocation.clearWatch(watchId.current);
          watchId.current = null;
        }
      } else if (wanted.current && watchId.current === null) {
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      // Never leave a watch running behind a closed page.
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    };
  }, [start]);

  return { state, fix, start, stop };
}
